import { authClient } from "@/lib/authClient";
import { RouteTable } from "@/lib/routes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@repo/ui/components/Button";
import { Card, CardContent } from "@repo/ui/components/Card";
import { FieldDescription, FieldGroup } from "@repo/ui/components/Field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/Form";
import { Input } from "@repo/ui/components/Input";
import { LoadingWrapper } from "@repo/ui/components/LoadingWrapper";
import { toast } from "@repo/ui/lib/toast";
import { useMutation } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router";
import { registerFormSchema, type RegisterFormData } from "./formSchema";

const defaultValues: RegisterFormData = {
  email: "",
  name: "",
  password: "",
  confirmPassword: "",
};

export const Register = () => {
  const navigate = useNavigate();
  const { data: session, refetch: refetchSession } = authClient.useSession();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues,
  });

  const { mutate: register, isPending } = useMutation({
    mutationFn: async ({ name, email, password }: RegisterFormData) => {
      const response = await authClient.signUp.email({
        name,
        email,
        password,
        requiresPasswordChange: false,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to create account");
      }

      return response;
    },
    onSuccess: async () => {
      await refetchSession();
      void navigate(RouteTable.dashboard());
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (session) return <Navigate to={RouteTable.dashboard()} />;

  return (
    <div className="bg-muted flex min-h-svh items-center justify-center p-6 md:p-10">
      <div className="flex w-full max-w-lg flex-col gap-6">
        <Card className="overflow-hidden p-0">
          <LoadingWrapper isLoading={isPending} overlayClassName="rounded-xl">
            <CardContent className="p-8">
              <Form {...form}>
                <form
                  noValidate
                  onSubmit={(e) => void form.handleSubmit((data) => register(data))(e)}
                >
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 className="text-primary text-2xl font-bold">Create your account</h1>
                      <p className="text-muted-foreground text-sm text-balance">
                        Enter your email below to create your account
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name *</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input placeholder="email@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Create a strong password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Re-enter the new password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" color="primary" disabled={form.formState.isSubmitting}>
                      {form.formState.isSubmitting && (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Create Account
                    </Button>

                    <FieldDescription className="text-center">
                      Already have an account? <Link to={RouteTable.login()}>Login</Link>
                    </FieldDescription>
                  </FieldGroup>
                </form>
              </Form>
            </CardContent>
          </LoadingWrapper>
        </Card>
      </div>
    </div>
  );
};
